import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { lovable } from "@/integrations/lovable/index";
import {
  whatsappRequestSignupOtp,
  whatsappVerifySignupOtp,
  whatsappCompleteSignup,
} from "@/lib/whatsapp.functions";
import { adminGetMyAccess } from "@/lib/admin-authz.functions";
import { captureSignupAttempt } from "@/lib/lead-partial.functions";
import { getAnonymousId } from "@/lib/identity/anonymousId";
import {
  whatsappRequestErrorCopy,
  whatsappVerifyErrorCopy,
} from "@/lib/whatsapp/error-messages";

export const Route = createFileRoute("/auth")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Sign in · TruWe" },
      { name: "description", content: "Sign in to your TruWe account to view your orders." },
      { property: "og:title", content: "Sign in · TruWe" },
      { property: "og:description", content: "Sign in to your TruWe account to view your orders and TruCoins." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

type Method = "whatsapp" | "email";

async function claimReferralSignupBonus(): Promise<void> {
  try {
    const { getStoredReferralCode, clearStoredReferralCode } = await import("@/lib/referral/capture");
    const code = getStoredReferralCode();
    if (!code) return;
    const { claimReferralSignupBonusServer } = await import("@/lib/loyalty/referral-signup.functions");
    const result = await claimReferralSignupBonusServer({ data: { referralCode: code } });
    // Terminal statuses that should clear the stored code (no retry helps).
    const terminal = new Set([
      "credited",
      "already_claimed",
      "phone_already_claimed",
      "self_referral",
      "invalid_code",
      "already_attributed_to_other",
      "no_reward_configured",
      "feature_disabled",
    ]);
    if (result.status === "credited" && result.creditedPaise > 0) {
      toast.success(`🎁 ₹${Math.round(result.creditedPaise / 100)} in TruCoins added to your wallet`);
    } else if (result.status === "phone_already_claimed") {
      toast.info("Welcome bonus already claimed on this phone number.");
    }
    if (terminal.has(result.status)) clearStoredReferralCode();
    // On transient errors (no_customer / rpc_error) keep the code so the
    // next authenticated action can retry.
  } catch {
    /* silent — bonus is a nice-to-have, never blocks signup */
  }
}



function AuthPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [method, setMethod] = useState<Method>("whatsapp");
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchAdminAccess = useServerFn(adminGetMyAccess);



  const OAUTH_NEXT_KEY = "oauth_next";

  function safeNextTarget(): string | null {
    if (typeof window === "undefined") return null;
    // OAuth redirects land on the origin root, so the ?next= param is lost;
    // it is stashed in sessionStorage before the provider hand-off.
    try {
      const stashed = window.sessionStorage.getItem(OAUTH_NEXT_KEY);
      if (stashed) {
        window.sessionStorage.removeItem(OAUTH_NEXT_KEY);
        if (stashed.startsWith("/") && !stashed.startsWith("//")) return stashed;
      }
    } catch { /* storage unavailable */ }
    const raw = new URLSearchParams(window.location.search).get("next");
    if (!raw) return null;
    // Only allow same-origin relative paths.
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
  }

  async function resolvePostLoginTarget(): Promise<{ to: string; search?: { section: string } }> {
    const next = safeNextTarget();
    if (next) return { to: next };
    try {
      const access = await fetchAdminAccess();
      if (access?.sections?.length) {
        const { adminLandingNav } = await import("@/lib/admin-invite");
        return adminLandingNav(access.sections);
      }
    } catch {
      // not admin or fetch failed — fall through
    }
    if (typeof window !== "undefined" && window.location.hostname === "admin.truwe.in") {
      return { to: "/admin" };
    }
    return { to: "/account" };
  }

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (data.user) {
        const target = await resolvePostLoginTarget();
        navigate({ to: target.to, search: target.search, replace: true } as never);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function normalizePhoneClient(input: string): string | null {
    const digits = input.replace(/[^\d]/g, "");
    if (!digits) return null;
    let d = digits;
    if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    if (d.length === 10) return `+91${d}`;
    return null;
  }

  async function claimSignupBonus() {
    await claimReferralSignupBonus();
  }



  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const normalizedPhone = normalizePhoneClient(phone);
        if (!normalizedPhone) {
          toast.error("Please enter a valid 10-digit Indian mobile number");
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin + "/account",
            data: { phone: normalizedPhone },
          },
        });
        if (error) throw error;
        await claimSignupBonus();
        toast.success("Account created. Check your email if confirmation is required.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
      router.invalidate();
      const target = await resolvePostLoginTarget();
      navigate({ to: target.to, search: target.search, replace: true } as never);
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-md flex-col justify-center px-4 py-12">
      <h1 className="font-display text-3xl font-semibold text-brand-deep">
        {method === "whatsapp"
          ? "Sign in with WhatsApp"
          : mode === "signin"
            ? "Sign in"
            : "Create account"}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {method === "whatsapp"
          ? "Get a one-time code on WhatsApp, no password to remember."
          : mode === "signin"
            ? "Access your order history and reorder in one tap."
            : "Save your details and track every order in one place."}
      </p>





      <div className="my-6 mb-4 grid grid-cols-2 gap-2 rounded-lg border border-border p-1">
        <button
          type="button"
          onClick={() => setMethod("whatsapp")}
          className={`rounded-md py-2 text-sm font-medium transition ${method === "whatsapp" ? "bg-brand-deep text-white" : "text-muted-foreground hover:bg-muted"}`}
        >
          WhatsApp OTP
        </button>
        <button
          type="button"
          onClick={() => setMethod("email")}
          className={`rounded-md py-2 text-sm font-medium transition ${method === "email" ? "bg-brand-deep text-white" : "text-muted-foreground hover:bg-muted"}`}
        >
          Email &amp; password
        </button>
      </div>

      {method === "email" ? (
        <>
          <form onSubmit={handleEmail} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {mode === "signup" && (
              <div>
                <Label htmlFor="phone">Mobile number</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  placeholder="10-digit Indian mobile"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  We use this to link guest orders and send delivery updates on WhatsApp.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete={mode === "signin" ? "current-password" : "new-password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" className="h-11 w-full" disabled={loading}>
              {mode === "signin" ? "Sign in" : "Create account"}
            </Button>
          </form>
          {mode === "signin" && (
            <button
              type="button"
              className="mt-4 text-center text-sm font-medium text-brand hover:underline disabled:opacity-50"
              disabled={loading}
              onClick={async () => {
                const target = email.trim();
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(target)) {
                  toast.error("Enter your email above first, then tap Forgot password");
                  return;
                }
                setLoading(true);
                try {
                  const { error } = await supabase.auth.resetPasswordForEmail(target, {
                    redirectTo: window.location.origin + "/reset-password",
                  });
                  if (error) throw error;
                  toast.success("Reset link sent", {
                    description: `Check ${target} for a link to choose a new password. It expires in 60 minutes.`,
                  });
                } catch (err: any) {
                  toast.error(err?.message || "Couldn't send the reset link");
                } finally {
                  setLoading(false);
                }
              }}
            >
              Forgot password?
            </button>
          )}
          <button
            type="button"
            className="mt-3 text-center text-sm text-muted-foreground hover:text-brand"
            onClick={() => setMode((m) => (m === "signin" ? "signup" : "signin"))}
          >
            {mode === "signin" ? "New to TruWe? Create an account" : "Already have an account? Sign in"}
          </button>

        </>
      ) : (
        <WhatsAppAuthForm onDone={async () => { router.invalidate(); const target = await resolvePostLoginTarget(); navigate({ to: target.to, search: target.search, replace: true } as never); }} />
      )}

      <div className="mt-8 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or continue with
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="mt-6 flex justify-center gap-4">
        <button
          type="button"
          aria-label="Continue with Google"
          className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background transition hover:bg-muted disabled:opacity-50"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            const next = new URLSearchParams(window.location.search).get("next");
            if (next && next.startsWith("/") && !next.startsWith("//")) {
              try { window.sessionStorage.setItem(OAUTH_NEXT_KEY, next); } catch { /* ignore */ }
            }
            const result = await lovable.auth.signInWithOAuth("google", {
              redirect_uri: window.location.origin,
            });
            if (result.error) {
              toast.error(result.error.message || "Couldn't start Google sign-in");
              setLoading(false);
              return;
            }
            if (result.redirected) return;
            router.invalidate();
            const target = await resolvePostLoginTarget();
            navigate({ to: target.to, search: target.search, replace: true } as never);
          }}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
            <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.63h6.45a5.52 5.52 0 0 1-2.39 3.62v3.01h3.87c2.26-2.09 3.57-5.16 3.57-8.81Z"/>
            <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.93-2.92l-3.87-3.01c-1.07.72-2.45 1.15-4.06 1.15-3.12 0-5.76-2.11-6.7-4.94H1.3v3.1A12 12 0 0 0 12 24Z"/>
            <path fill="#FBBC05" d="M5.3 14.28A7.2 7.2 0 0 1 4.92 12c0-.79.14-1.56.38-2.28v-3.1H1.3a12 12 0 0 0 0 10.76l4-3.1Z"/>
            <path fill="#EA4335" d="M12 4.78c1.76 0 3.35.61 4.59 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.3 6.62l4 3.1c.94-2.83 3.58-4.94 6.7-4.94Z"/>
          </svg>
        </button>
        <button
          type="button"
          aria-label="Continue with Apple"
          className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background transition hover:bg-muted disabled:opacity-50"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            const next = new URLSearchParams(window.location.search).get("next");
            if (next && next.startsWith("/") && !next.startsWith("//")) {
              try { window.sessionStorage.setItem(OAUTH_NEXT_KEY, next); } catch { /* ignore */ }
            }
            const result = await lovable.auth.signInWithOAuth("apple", {
              redirect_uri: window.location.origin,
            });
            if (result.error) {
              toast.error(result.error.message || "Couldn't start Apple sign-in");
              setLoading(false);
              return;
            }
            if (result.redirected) return;
            router.invalidate();
            const target = await resolvePostLoginTarget();
            navigate({ to: target.to, search: target.search, replace: true } as never);
          }}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6 fill-foreground" aria-hidden="true">
            <path d="M17.05 12.54c-.03-2.89 2.36-4.28 2.47-4.35-1.35-1.97-3.44-2.24-4.18-2.27-1.78-.18-3.47 1.05-4.37 1.05-.9 0-2.29-1.02-3.77-1-1.94.03-3.72 1.13-4.72 2.86-2.01 3.49-.51 8.66 1.45 11.5.96 1.39 2.1 2.94 3.6 2.88 1.45-.06 2-.93 3.75-.93s2.25.93 3.77.9c1.56-.03 2.55-1.41 3.5-2.8 1.1-1.61 1.55-3.17 1.58-3.25-.04-.02-3.03-1.16-3.08-4.59ZM14.16 4.06c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.55.77-3.38 1.74-.74.85-1.39 2.23-1.22 3.54 1.29.1 2.6-.65 3.41-1.62Z"/>
          </svg>
        </button>
      </div>

      <Link to="/" className="mt-4 text-center text-xs text-muted-foreground hover:text-brand">
        ← Back to store
      </Link>

    </main>
  );
}

type Stage = "phone" | "code" | "email";

function WhatsAppAuthForm({ onDone }: { onDone: () => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [fullName, setFullName] = useState("");
  const [signupTicket, setSignupTicket] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("phone");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [providerDisabled, setProviderDisabled] = useState(false);
  const timerRef = useRef<number | null>(null);

  const requestOtp = useServerFn(whatsappRequestSignupOtp);
  const verifyOtpFn = useServerFn(whatsappVerifySignupOtp);
  const completeSignupFn = useServerFn(whatsappCompleteSignup);
  const captureAttempt = useServerFn(captureSignupAttempt);

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [cooldown]);

  async function sendCode(isResend = false) {
    if (busy || cooldown > 0) return;
    setBusy(true);
    try {
      if (!isResend) {
        // Show abandoned signups in Admin → Leads too.
        try {
          const anonId = getAnonymousId();
          if (anonId) void captureAttempt({ data: { anonymousId: anonId, phone, source: "auth:whatsapp" } });
        } catch { /* never block sign-in */ }
      }
      const r = await requestOtp({ data: { phone } });
      if (r.ok) {
        toast.success(isResend ? "New code sent on WhatsApp" : "Code sent on WhatsApp");
        setStage("code");
        setCode("");
        setCooldown(r.cooldownSeconds ?? 45);
      } else {
        if (r.error === "provider_disabled") setProviderDisabled(true);
        const retry = "retryAfterSec" in r ? r.retryAfterSec : undefined;
        const copy = whatsappRequestErrorCopy(r.error, retry);
        toast.error(copy.title, { description: copy.hint });
        if (r.error === "rate_limited") setCooldown(retry ?? 60);
      }
    } finally {
      setBusy(false);
    }
  }

  async function completeSession(hashedToken: string) {
    const { error } = await supabase.auth.verifyOtp({
      token_hash: hashedToken,
      type: "magiclink",
    });
    if (error) {
      const copy = whatsappVerifyErrorCopy("session_error");
      toast.error(copy.title, { description: copy.hint });
      return false;
    }
    toast.success("Signed in");
    onDone();
    return true;
  }

  async function verify() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await verifyOtpFn({ data: { phone, code } });
      if (r.ok) {
        await completeSession(r.hashedToken);
        return;
      }
      if (r.error === "email_required" && "signupTicket" in r && r.signupTicket) {
        setSignupTicket(r.signupTicket);
        setStage("email");
        toast.info("One last step, tell us where to email your receipts.");
        return;
      }
      const copy = whatsappVerifyErrorCopy(r.error);
      toast.error(copy.title, { description: copy.hint });
      if (r.error === "expired" || r.error === "too_many_attempts" || r.error === "no_active_code") {
        setCode("");
        setCooldown(0);
      }
    } finally {
      setBusy(false);
    }
  }

  async function completeSignup() {
    if (busy || !signupTicket) return;
    setBusy(true);
    try {
      const r = await completeSignupFn({
        data: { signupTicket, email: emailInput.trim() || undefined, fullName: fullName || undefined },
      });
      if (!r.ok) {
        const copy = whatsappVerifyErrorCopy(r.error);
        toast.error(copy.title, { description: copy.hint });
        if (r.error === "ticket_invalid") {
          setStage("phone");
          setCode("");
          setSignupTicket(null);
        }
        return;
      }

      await claimReferralSignupBonus();
      await completeSession(r.hashedToken);
    } finally {
      setBusy(false);
    }
  }

  if (providerDisabled) {
    return (
      <p className="rounded-md border border-border bg-muted p-4 text-sm text-muted-foreground">
        WhatsApp OTP is not configured for this environment. Please use email
        &amp; password to sign in, or try again later.
      </p>
    );
  }

  if (stage === "email") {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          ✓ WhatsApp number verified. Add your name to finish, email is optional.
        </div>
        <div>
          <Label htmlFor="wa-name">Your name</Label>
          <Input
            id="wa-name"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="e.g. Priya Sharma"
          />
        </div>
        <div>
          <Label htmlFor="wa-email">
            Email <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="wa-email"
            type="email"
            autoComplete="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="you@example.com"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Add it to receive order receipts and delivery updates by email, we'll still message you on WhatsApp.
          </p>
        </div>
        <Button
          type="button"
          className="h-11 w-full"
          onClick={completeSignup}
          disabled={busy}
        >
          {busy ? "Creating account…" : "Create account & continue"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="wa-phone">Mobile number</Label>
        <Input
          id="wa-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="10-digit Indian mobile"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={stage === "code"}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          We'll message a one-time code on WhatsApp. Standard messaging rates may apply.
        </p>
      </div>

      {stage === "code" && (
        <div>
          <Label htmlFor="wa-code">6-digit code</Label>
          <Input
            id="wa-code"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>Didn't get it? Check WhatsApp on this number.</span>
            <button
              type="button"
              onClick={() => sendCode(true)}
              disabled={busy || cooldown > 0}
              className="font-medium text-brand disabled:opacity-40"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        </div>
      )}

      <Button
        type="button"
        className="h-11 w-full"
        onClick={() => (stage === "phone" ? sendCode(false) : verify())}
        disabled={busy || (stage === "phone" ? !phone : code.length !== 6)}
      >
        {stage === "phone"
          ? busy
            ? "Sending…"
            : "Send OTP on WhatsApp"
          : busy
            ? "Verifying…"
            : "Verify & continue"}
      </Button>

      {stage === "code" && (
        <button
          type="button"
          onClick={() => { setStage("phone"); setCode(""); }}
          className="w-full text-center text-xs text-muted-foreground hover:text-brand"
        >
          Change number
        </button>
      )}
    </div>
  );
}
